import type {
  AddDashboard2RawParamsT,
  Dashboard2BaseListT,
  Dashboard2FolderT,
  Dashboard2ListT,
  Dashboard2RowT,
  Dashboard2T,
  FeedbacksSettingT,
  MoveChartInDashboard2RowParamsT,
} from '@/features/Dashboard/types/types';
import type { StaticDashboard2WorkspaceInfoRaw } from '@/features/StaticDashboard2/types';
import { FILTER_OPERATOR } from '@/types/dataTableConst';
import { omitEmpty } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { defaultAxios } from '..';

// 마지막 행에 Push 할 때 사용하는 상수
export const MAX_LAST_ROW_POS = 1000000;

const dashboardService = {
  async fetchDashboardTreeList(name?: null | string): Promise<Dashboard2ListT> {
    const wa = getCurrWorkspaceAlias();

    const res = await defaultAxios.get(`/workspaces/${wa}/dashboard_folders3`, {
      params: {
        name,
      },
    });

    return res.data;
  },

  async fetchDashboardList(params: {
    sort_by: 'updated_at' | 'starred_at';
    order_by: 'asc' | 'desc';
    starred?: boolean;
    ids?: string[];
    limit?: number;
  }): Promise<Dashboard2BaseListT> {
    const wa = getCurrWorkspaceAlias();
    const ids = params.ids?.join(FILTER_OPERATOR.OR);

    const newParams = {
      ...params,
      ids,
    };

    const res = await defaultAxios.get(`/workspaces/${wa}/dashboards3`, {
      params: omitEmpty(newParams),
    });
    return res.data;
  },

  async fetchDashboardFolder(folderAlias?: string): Promise<Dashboard2FolderT> {
    if (!folderAlias) {
      throw new Error('folderAlias is required');
    }

    const res = await defaultAxios.get(`/dashboard_folders3/${folderAlias}`);
    return res.data;
  },

  async fetchDashboardsInFolder(
    folderAlias: string,
    name?: null | string,
  ): Promise<Dashboard2ListT> {
    const res = await defaultAxios.get(
      `/dashboard_folders3/${folderAlias}/dashboards3`,
      {
        params: {
          name,
        },
      },
    );
    return res.data;
  },

  async deleteDashboards(dashboardAliases: string[]): Promise<void> {
    const wa = getCurrWorkspaceAlias();
    await defaultAxios.patch(`/workspaces/${wa}/dashboards3/delete`, {
      dashboards: dashboardAliases,
    });
  },

  async deleteDashboardFolder(folderAlias: string): Promise<void> {
    const res = await defaultAxios.delete(`/dashboard_folders3/${folderAlias}`);

    return res.data;
  },

  async moveDashboardToFolder(
    folderAlias: string,
    dashboardIds: string[],
  ): Promise<void> {
    await defaultAxios.patch(`/dashboard_folders3/${folderAlias}/dashboards3`, {
      dashboards: dashboardIds,
    });
  },

  async changeDashboardFolderName(
    folderAlias: string,
    name: string,
  ): Promise<void> {
    await defaultAxios.patch(`/dashboard_folders3/${folderAlias}/name`, {
      name,
    });
  },

  async createDashboardFolder(name: string): Promise<void> {
    const wa = getCurrWorkspaceAlias();

    await defaultAxios.post(`/workspaces/${wa}/dashboard_folder3`, {
      name,
    });
  },

  async createDashboardInFolder(
    folderAlias: string,
    title: string,
  ): Promise<Dashboard2T> {
    const res = await defaultAxios.post(
      `/dashboard_folders3/${folderAlias}/dashboard3`,
      {
        title,
      },
    );

    return res.data.dashboard;
  },

  async fetchDashboard(dashboardAlias: string): Promise<Dashboard2T> {
    const res = await defaultAxios.get(`/dashboards3/${dashboardAlias}`);
    return res.data.dashboard;
  },

  async deleteDashboard(dashboardAlias: string): Promise<void> {
    await defaultAxios.delete(`/dashboards3/${dashboardAlias}`);
  },

  async duplicateDashboard(dashboardAlias: string): Promise<Dashboard2T> {
    const res = await defaultAxios.post(
      `/dashboards3/${dashboardAlias}/duplicate`,
    );
    return res.data.dashboard;
  },

  async createDashboard(title: string): Promise<Dashboard2T> {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(`/workspaces/${wa}/dashboard3`, {
      title,
    });
    return res.data.dashboard;
  },

  async changeDashboardTitle(
    dashboardAlias: string,
    title: string,
  ): Promise<Dashboard2T> {
    const res = await defaultAxios.patch(
      `/dashboards3/${dashboardAlias}/title`,
      {
        title,
      },
    );
    return res.data.dashboard;
  },

  async addDashboardRow(
    dashboardAlias: string,
    params: AddDashboard2RawParamsT,
  ): Promise<{ row_id: string }> {
    const res = await defaultAxios.post(
      `/dashboards3/${dashboardAlias}/row`,
      params,
    );
    return res.data;
  },

  async changeDashboardRowText(
    dashboardAlias: string,
    columnId: string,
    text: string,
  ): Promise<Dashboard2RowT> {
    const res = await defaultAxios.patch(
      `/dashboards3/${dashboardAlias}/columns/${columnId}/text`,
      {
        text,
      },
    );
    return res.data.row;
  },

  async changeDashboardFeedbacksSetting(
    dashboardAlias: string,
    columnId: string,
    feedbacksSetting: FeedbacksSettingT,
  ): Promise<void> {
    await defaultAxios.patch(
      `/dashboards3/${dashboardAlias}/columns/${columnId}/feedbacks_setting`,
      feedbacksSetting,
    );
  },

  async changeDashboardFeedbacksSettingByColumn(
    dashboardAlias: string,
    _rowId: string,
    columnId: string,
    feedbacksSetting: FeedbacksSettingT,
  ): Promise<void> {
    await defaultAxios.patch(
      `/dashboards3/${dashboardAlias}/columns/${columnId}/feedbacks_setting`,
      {
        feedbacks_setting: feedbacksSetting,
      },
    );
  },

  async moveDashboardRow(
    dashboardAlias: string,
    rowId: string,
    pos: number,
  ): Promise<void> {
    const res = await defaultAxios.patch(
      `/dashboards3/${dashboardAlias}/rows/${rowId}`,
      {
        pos,
      },
    );

    return res.data;
  },

  async deleteDashboardRow(dashboardAlias: string, rowId: string) {
    const res = await defaultAxios.delete(
      `/dashboards3/${dashboardAlias}/rows/${rowId}`,
    );

    return res.data;
  },

  async addChartInDashboardRow(
    dashboardAlias: string,
    rowId: string,
    chartId: string,
    pos?: number,
  ): Promise<void> {
    const res = await defaultAxios.post(
      `/dashboards3/${dashboardAlias}/rows/${rowId}/chart`,
      {
        chart_id: chartId,
        pos,
      },
    );

    return res.data;
  },

  async moveColumnInDashboardRow(
    dashboardAlias: string,
    rowId: string,
    columnId: string,
    params: MoveChartInDashboard2RowParamsT,
  ): Promise<void> {
    const res = await defaultAxios.patch(
      `/dashboards3/${dashboardAlias}/rows/${rowId}/columns/${columnId}`,
      {
        pos: params.pos,
        row_id: params.destinationRowId,
      },
    );

    return res.data;
  },

  async deleteColumnInDashboardRow(
    dashboardAlias: string,
    rowId: string,
    colId: string,
  ): Promise<void> {
    const res = await defaultAxios.delete(
      `/dashboards3/${dashboardAlias}/rows/${rowId}/columns/${colId}`,
    );

    return res.data;
  },

  async validateChartAdditionToDashboard(
    dashboardAlias: string,
    chartId: string,
  ) {
    const res = await defaultAxios.get(
      `/dashboards3/${dashboardAlias}/charts/${chartId}`,
    );

    return res.data;
  },

  async addMultipleChartInDashboard(
    dashboardAlias: string,
    chartAliases: string[],
  ) {
    const res = await defaultAxios.post(
      `/dashboards3/${dashboardAlias}/charts`,
      {
        charts: chartAliases,
      },
    );

    return res.data;
  },

  async moveDashboartToRoot(dashboardAliases: string[]) {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.patch(`/workspaces/${wa}/dashboards3/move`, {
      dashboards: dashboardAliases,
    });
    return res.data;
  },

  async fetchStaticDashboardData(
    dashboardAlias: string,
  ): Promise<StaticDashboard2WorkspaceInfoRaw> {
    const res = await defaultAxios.get(`/dashboards3/${dashboardAlias}/export`);
    return res.data;
  },

  async changeDashboardRowHeight(
    dashboardAlias: string,
    rowId: string,
    height: string,
  ): Promise<void> {
    await defaultAxios.patch(
      `/dashboards3/${dashboardAlias}/rows/${rowId}/height`,
      { height },
    );
  },

  async updateDashboardStarred(
    dashboardAlias: string,
    starred: boolean,
  ): Promise<void> {
    await defaultAxios.patch(`/dashboards3/${dashboardAlias}/starred`, {
      starred,
    });
  },

  async refreshAnalyses(
    dashboardAlias: string,
    analysis_aliases: string[],
  ): Promise<void> {
    await defaultAxios.post(`/dashboards3/${dashboardAlias}/analysis/refresh`, {
      analysis_aliases,
    });
  },
};

export default dashboardService;
